import * as Tabs from '@radix-ui/react-tabs';
import { TouchApp } from '@styled-icons/material';
import classNames from 'classnames';
import { useState } from 'react';
import { hasProperty, isGlowingButton, isInputBlock, isTextBlock } from '../../editorTools/queries';
import { CollectionDataPluginOptions } from '../../nodes/collection/types';
import { getTPlugin, useTPlateEditorRef } from '../../plate';
import {
  ELEMENT_ACCORDION,
  ELEMENT_ACCORDION_ITEM,
  ELEMENT_BUTTON,
  ELEMENT_COLLECTION,
  ELEMENT_COLUMN,
  ELEMENT_CONTAINER,
  ELEMENT_IMAGE,
  ELEMENT_MEDIA_EMBED,
  KEY_COLLECTION_CONNECT_DATA,
} from '../../pluginKeys';
import { useSelectedElementEntry } from '../../plugins/elementSelection';
import { cn } from '../../utils/classnames';
import { SideToolbarCollapsible } from './components/SideToolbarCollapsible';
import AccordionAddItem from './items/AccordionAddItem';
import AccordionStyle from './items/AccordionStyle';
import Align from './items/Align';
import Background from './items/Background';
import BlockAnimation from './items/BlockAnimation';
import BlockType from './items/BlockType';
import { BorderRadius } from './items/BorderRadius';
import { BoxShadow } from './items/BoxShadow';
import ButtonIcon from './items/ButtonIcon';
import ButtonStyle from './items/ButtonStyle';
import Collection from './items/Collection';
import ColumnStacking from './items/ColumnStacking';
import ConnectData from './items/ConnectData';
import ContainerStyle from './items/ContainerStyle';
import EmbedAspectRatio from './items/EmbedAspectRatio';
import EmbedSource from './items/EmbedSource';
import FontFamily from './items/FontFamily';
import { FontSize } from './items/FontSize';
import { FontWeight } from './items/FontWeight';
import Footer from './items/Footer';
import Form from './items/Form';
import { Gap } from './items/Gap';
import GlowingButtonBackground from './items/GlowingButtonBackground';
import HightlightColor from './items/HighlightColor';
import ImageSeo from './items/ImageSeo';
import ImageSource from './items/ImageSource';
import InputAttribute from './items/InputAttribute';
import InputLabel from './items/InputLabel';
import InputPlaceholder from './items/InputPlaceholder';
import InputRequired from './items/InputRequired';
import InputType from './items/InputType';
import LeafAnimation from './items/LeafAnimation';
import { LetterSpacing } from './items/LetterSpacing';
import { LineHeight } from './items/LineHeight';
import Link from './items/Link';
import { Margin } from './items/Margin';
import { Overflow } from './items/Overflow';
import { Padding } from './items/Padding';
import Sizing from './items/Sizing';
import { SyncedContainerMenu } from './items/SyncedContainer';
import TextColor from './items/TextColor';
import TextFormat from './items/TextFormat';
import TextStyle from './items/TextStyle';
import VerticalAlign from './items/VerticalAlign';
import { DesktopVisibility, MobileVisibility } from './items/Visibility';
import { shouldShowLeafAnimation, shouldShowLink, shouldShowSizing } from './queries';
import styles from './SideToolbar.module.css';

function InputSection() {
  const editor = useTPlateEditorRef();
  const elementEntry = useSelectedElementEntry();

  if (!elementEntry) return null;

  const [element] = elementEntry;

  if (!isInputBlock(editor, element)) return null;

  return (
    <div className={styles.section}>
      <div className="with-light-text-color is-size-7 has-text-weight-semibold mb-2">
        INPUT BLOCK
      </div>
      <InputType />
      {hasProperty(editor, element, 'label') && <InputLabel />}
      {hasProperty(editor, element, 'placeholder') && <InputPlaceholder />}
      {hasProperty(editor, element, 'required') && <InputRequired />}
      <hr style={{ margin: 0 }} />
    </div>
  );
}

export function SideToolbar() {
  const editor = useTPlateEditorRef();
  const elementEntry = useSelectedElementEntry();
  const { options } = getTPlugin<CollectionDataPluginOptions>(editor, KEY_COLLECTION_CONNECT_DATA);

  const { showSchema } = options as CollectionDataPluginOptions;

  const tabsList = ['basic', 'advanced'];
  const [tabSelected, setTabSelected] = useState(tabsList[0]);

  const renderItems = () => {
    if (!elementEntry) {
      return (
        <div className={styles.tabRoot}>
          <div
            className={classNames(
              styles.label,
              styles.card,
              styles.section,
              'is-flex is-flex-direction-column is-align-items-center p-3'
            )}
          >
            <TouchApp size={20} style={{ marginBottom: 16 }} />
            <span>Please select an element</span>
          </div>
        </div>
      );
    }

    const [element] = elementEntry;

    return (
      <Tabs.Root defaultValue={tabSelected} value={tabSelected} className={styles.tabRoot}>
        <Tabs.List className={styles.tabList}>
          {tabsList.map((tab) => (
            <Tabs.Trigger
              key={tab}
              value={tab}
              onClick={() => setTabSelected(tab)}
              className={styles.tabTrigger}
            >
              <span>{tab}</span>
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value={tabsList[0]} className={styles.tabContent}>
          <InputSection />
          {/* Only show section header when there's an input block */}
          {isInputBlock(editor, element) && (
            <div
              className={classNames(
                'with-light-text-color is-size-7 has-text-weight-semibold mb-2',
                styles.section
              )}
            >
              LAYOUT BLOCK
            </div>
          )}
          <BlockType />
          {hasProperty(editor, element, 'transclusion_reference_id') && <SyncedContainerMenu />}
          {element.type === ELEMENT_COLLECTION && <Collection />}
          {isTextBlock(editor, element) && <TextFormat />}
          {hasProperty(editor, element, 'align') && <Align />}
          {element.type === ELEMENT_CONTAINER && <ContainerStyle />}
          {element.type === ELEMENT_BUTTON && <ButtonStyle />}
          {element.type === ELEMENT_BUTTON && isGlowingButton(editor, element) && (
            <GlowingButtonBackground />
          )}
          {[ELEMENT_ACCORDION, ELEMENT_ACCORDION_ITEM].includes(element.type) && (
            <>
              <AccordionStyle />
              <AccordionAddItem />
            </>
          )}
          {element.type === ELEMENT_MEDIA_EMBED && <EmbedSource />}
          {shouldShowSizing(elementEntry) && <Sizing />}
          {isTextBlock(editor, element) && (
            <>
              <TextStyle />
              <FontFamily />
              <TextColor />
              <HightlightColor />
            </>
          )}
          {element.type === ELEMENT_IMAGE && <ImageSource />}
          {hasProperty(editor, element, 'vertical_align') && <VerticalAlign />}
          {hasProperty(editor, element, 'form_id') && <Form />}
          {shouldShowLink(editor, elementEntry) && <Link setToolbarTab={setTabSelected} />}
          {element.type === ELEMENT_IMAGE && <ImageSeo />}

          {element.type === ELEMENT_BUTTON && <ButtonIcon />}
          {(hasProperty(editor, element, 'background') ||
            hasProperty(editor, element, 'backgroundImage') ||
            hasProperty(editor, element, 'backgroundVideo')) && (
            <Background
              color={hasProperty(editor, element, 'background')}
              image={hasProperty(editor, element, 'backgroundImage')}
              video={hasProperty(editor, element, 'backgroundVideo')}
            />
          )}
        </Tabs.Content>

        <Tabs.Content value={tabsList[1]} className={styles.tabContent}>
          {(hasProperty(editor, element, 'padding') ||
            hasProperty(editor, element, 'margin') ||
            hasProperty(editor, element, 'gap')) && (
            <div className="flex flex-col gap-1">
              <div className={styles.groupLabel}>Layout</div>
              {hasProperty(editor, element, 'padding') && <Padding />}
              {hasProperty(editor, element, 'margin') && <Margin />}
              {hasProperty(editor, element, 'gap') && <Gap />}
              {hasProperty(editor, element, 'overflow') && <Overflow />}
              <hr className="w-full m-0 h-px bg-hr" />
            </div>
          )}
          {isTextBlock(editor, element) && (
            <SideToolbarCollapsible
              titleText="Text"
              contentClassName={cn(styles.section, 'space-y-2')}
            >
              <FontWeight />
              <FontSize />
              <LetterSpacing />
              <LineHeight />
            </SideToolbarCollapsible>
          )}
          {(hasProperty(editor, element, 'border_radius') ||
            hasProperty(editor, element, 'box_shadow')) && (
            <div className="flex flex-col gap-1">
              <div className={styles.groupLabel}>Styles</div>
              {hasProperty(editor, element, 'border_radius') && <BorderRadius />}
              {hasProperty(editor, element, 'box_shadow') && <BoxShadow />}
              <hr className="w-full m-0 h-px bg-hr" />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <div className={styles.groupLabel}>Responsiveness</div>
            {hasProperty(editor, element, 'mobile_hide') && <MobileVisibility />}
            {element.type === ELEMENT_COLUMN && <ColumnStacking />}
            {element.type === ELEMENT_MEDIA_EMBED && <EmbedAspectRatio />}
            {hasProperty(editor, element, 'desktop_hide') && <DesktopVisibility />}
            <hr className="w-full m-0 h-px bg-hr" />
          </div>
          {showSchema && hasProperty(editor, element, 'connect_data') && <ConnectData />}
          {hasProperty(editor, element, 'animation') && <BlockAnimation />}
          {shouldShowLeafAnimation(editor, elementEntry) && <LeafAnimation />}
          <InputAttribute />
        </Tabs.Content>
      </Tabs.Root>
    );
  };

  return (
    <div className={styles.toolbar}>
      {renderItems()}
      <Footer />
    </div>
  );
}
